import { useQuery, useQueryClient } from "@tanstack/react-query";

import { API_BASE_URL } from "@/config";
import type { FloorPlanDocument } from "@/lib/shows/floorPlan";
import {
  deletePublishedShowArtifacts,
  readPublishedShowArtifacts,
  type SavedPublishedShowArtifacts,
  savePublishedShowArtifacts,
} from "@/lib/shows/publicShowArtifactCache";
import { isRecord, isStringOrNull, optionalFields, validatedJson } from "@/lib/ui/validatedJson";

export type PublicShowVendor = {
  participation_id: string;
  name: string;
  table_ids: string[];
  searchable: boolean;
  buylist_slug?: string | null;
};

export type PublicPlanAssignment = {
  table_id: string;
  participation_id: string;
  vendor_name: string;
  searchable: boolean;
};

export type PublicShowPlan = {
  revision: number;
  published_at: string;
  document: FloorPlanDocument | string;
  assignments: PublicPlanAssignment[];
};

export type PublicShowLink = {
  public_id: string;
  name: string;
  phase: "upcoming" | "live" | "completed";
};

export type PublicShowOrganizer = {
  display_name: string;
  logo_url?: string | null;
  other_listed_shows?: PublicShowLink[] | null;
};

/** One Table option on offer. The server publishes these only while applications are open. */
export type PublicTableOption = {
  public_id: string;
  name: string;
  session_ids: string[];
  configuration: string;
  unit_fee: string;
  currency: string;
  available: boolean;
};

export type PublicShow = {
  public_id: string;
  name: string;
  description?: string | null;
  venue_name: string;
  venue_address: string;
  timezone?: string;
  games?: string[];
  state: "published" | "canceled";
  phase: "upcoming" | "live" | "completed";
  admission?: string | null;
  vendor_admission_mode?: "applications_open" | "invite_only" | null;
  public_table_details?: string | null;
  /** Whether a Vendor may apply unbidden. False for an invite-only Show and for no mode at all. */
  applications_open?: boolean;
  table_options?: PublicTableOption[];
  poster_url?: string | null;
  external_action_url?: string | null;
  external_action_label?: string | null;
  organizer?: PublicShowOrganizer | null;
  cancellation_reason?: string | null;
  sessions?: Array<{
    public_id: string;
    position: number;
    starts_at: string;
    ends_at: string;
  }>;
  public_version: number;
  plan_state: "unavailable" | "empty" | "populated";
  plan: PublicShowPlan | null;
  roster: PublicShowVendor[];
};

type PublicShowDetails = Omit<PublicShow, "plan" | "roster">;
type CachedPlan = Omit<PublicShowPlan, "assignments"> | null;
type CachedShow = SavedPublishedShowArtifacts<
  PublicShowDetails,
  PublicShowVendor[],
  CachedPlan,
  PublicPlanAssignment[]
>;

class PublicShowRequestError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
  }
}

function isVendor(value: unknown): value is PublicShowVendor {
  return isRecord(value) && typeof value.participation_id === "string"
    && typeof value.name === "string"
    && Array.isArray(value.table_ids) && value.table_ids.every((id) => typeof id === "string")
    && typeof value.searchable === "boolean"
    && (value.buylist_slug === undefined || isStringOrNull(value.buylist_slug));
}

function isPlanAssignment(value: unknown): value is PublicPlanAssignment {
  return isRecord(value) && typeof value.table_id === "string"
    && typeof value.participation_id === "string" && typeof value.vendor_name === "string"
    && typeof value.searchable === "boolean";
}

function isPlan(value: unknown): value is PublicShowPlan {
  return isRecord(value) && typeof value.revision === "number"
    && typeof value.published_at === "string"
    && (typeof value.document === "string" || isRecord(value.document))
    && Array.isArray(value.assignments) && value.assignments.every(isPlanAssignment);
}

function isShowLink(value: unknown): value is PublicShowLink {
  return isRecord(value) && typeof value.public_id === "string"
    && typeof value.name === "string"
    && (value.phase === "upcoming" || value.phase === "live" || value.phase === "completed");
}

function isOrganizer(value: unknown): value is PublicShowOrganizer {
  return isRecord(value) && typeof value.display_name === "string"
    && (value.logo_url === undefined || isStringOrNull(value.logo_url))
    && (value.other_listed_shows === undefined || value.other_listed_shows === null
      || (Array.isArray(value.other_listed_shows) && value.other_listed_shows.every(isShowLink)));
}

function isTableOption(value: unknown): value is PublicTableOption {
  return isRecord(value) && typeof value.public_id === "string" && typeof value.name === "string"
    && typeof value.configuration === "string" && typeof value.unit_fee === "string"
    && typeof value.currency === "string" && typeof value.available === "boolean"
    && Array.isArray(value.session_ids) && value.session_ids.every((id) => typeof id === "string");
}

function isSession(value: unknown): value is NonNullable<PublicShow["sessions"]>[number] {
  return isRecord(value) && typeof value.public_id === "string"
    && typeof value.position === "number"
    && typeof value.starts_at === "string" && typeof value.ends_at === "string";
}

function isPublicShow(value: unknown): value is PublicShow {
  return isRecord(value) && typeof value.public_id === "string" && typeof value.name === "string"
    && typeof value.venue_name === "string" && typeof value.venue_address === "string"
    && (value.state === "published" || value.state === "canceled")
    && (value.phase === "upcoming" || value.phase === "live" || value.phase === "completed")
    && typeof value.public_version === "number"
    && (value.plan_state === "unavailable" || value.plan_state === "empty"
      || value.plan_state === "populated")
    && (value.plan === null || isPlan(value.plan))
    && Array.isArray(value.roster) && value.roster.every(isVendor)
    && optionalFields(
      value,
      [
        "description",
        "admission",
        "public_table_details",
        "poster_url",
        "external_action_url",
        "external_action_label",
        "cancellation_reason",
      ],
      isStringOrNull,
    )
    && (value.vendor_admission_mode === undefined
      || value.vendor_admission_mode === null
      || value.vendor_admission_mode === "applications_open"
      || value.vendor_admission_mode === "invite_only")
    && (value.applications_open === undefined || typeof value.applications_open === "boolean")
    && (value.table_options === undefined
      || (Array.isArray(value.table_options) && value.table_options.every(isTableOption)))
    && (value.timezone === undefined || typeof value.timezone === "string")
    && (value.games === undefined
      || (Array.isArray(value.games) && value.games.every((game) => typeof game === "string")))
    && (value.organizer === undefined || value.organizer === null || isOrganizer(value.organizer))
    && (value.sessions === undefined
      || (Array.isArray(value.sessions) && value.sessions.every(isSession)));
}

function toArtifacts(show: PublicShow) {
  const { plan, roster, ...details } = show;
  if (!plan) {
    return {
      showId: show.public_id,
      state: "published" as const,
      details,
      roster,
      plan: null,
      assignments: [] as PublicPlanAssignment[],
    };
  }
  const { assignments, ...planWithoutAssignments } = plan;
  return {
    showId: show.public_id,
    state: "published" as const,
    details,
    roster,
    plan: planWithoutAssignments,
    assignments,
  };
}

function fromArtifacts(saved: CachedShow): PublicShow {
  return {
    ...saved.details,
    roster: saved.roster,
    plan: saved.plan ? { ...saved.plan, assignments: saved.assignments } : null,
  };
}

/** Public Show data stays unauthenticated and falls back only to a previously Published snapshot. */
export function usePublicShow(showId: string | undefined) {
  const queryClient = useQueryClient();
  const cacheQueryKey = ["public-show-cache", showId] as const;
  const evict = async () => {
    await deletePublishedShowArtifacts(showId!);
    await queryClient.cancelQueries({ queryKey: cacheQueryKey });
    queryClient.setQueryData(cacheQueryKey, null);
  };
  const cached = useQuery({
    queryKey: cacheQueryKey,
    enabled: !!showId,
    queryFn: () =>
      readPublishedShowArtifacts<
        PublicShowDetails,
        PublicShowVendor[],
        CachedPlan,
        PublicPlanAssignment[]
      >(showId!),
    retry: false,
  });
  const network = useQuery({
    queryKey: ["public-show", showId],
    enabled: !!showId,
    queryFn: async (): Promise<PublicShow> => {
      const response = await fetch(`${API_BASE_URL}/shows/${encodeURIComponent(showId!)}`);
      if (!response.ok) {
        if (response.status === 404) await evict();
        throw new PublicShowRequestError(
          response.status === 404
            ? "This Show is not available."
            : "The Show could not be loaded.",
          response.status,
        );
      }
      const show = await validatedJson(response, isPublicShow, "The Show response was invalid.");
      if (show.state === "published") await savePublishedShowArtifacts(toArtifacts(show));
      else await evict();
      return show;
    },
    retry: (failures, error) =>
      !(error instanceof PublicShowRequestError && error.status === 404) && failures < 2,
    retryDelay: (attempt) => Math.min(250 * 2 ** attempt, 1_000),
  });

  const mayUseCache = network.isError
    && !(network.error instanceof PublicShowRequestError && network.error.status === 404);
  const fallback = mayUseCache && cached.data ? fromArtifacts(cached.data) : null;
  const show = network.data ?? fallback;

  return {
    show,
    savedAt: network.data ? null : fallback ? cached.data?.savedAt ?? null : null,
    isLoading: !show && (network.isLoading || cached.isLoading),
    error: !show && network.isError && !cached.isLoading ? network.error : null,
    refetch: network.refetch,
  };
}
